/**
 * 翻訳フック
 * i18n翻訳を使用するためのカスタムフック
 * デフォルト言語: 英語
 */
import { useCallback, useState, createContext, useContext, ReactNode } from 'react';
import { en, ja, Translations, Locale, defaultLocale } from '../i18n';

/** 言語別翻訳マップ */
const translations: Record<Locale, Translations> = { en, ja };

/**
 * ドット記法でネストされたオブジェクトから値を取得
 * @param obj - 翻訳オブジェクト
 * @param path - ドット区切りのパス（例: "home.title"）
 * @param params - 置換パラメータ（例: { count: 10 }）
 */
function getNestedValue(
  obj: Translations,
  path: string,
  params?: Record<string, string | number>
): string {
  const keys = path.split('.');
  let result: unknown = obj;

  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = (result as Record<string, unknown>)[key];
    } else {
      // キーが見つからない場合はパスをそのまま返す
      console.warn(`Translation key not found: ${path}`);
      return path;
    }
  }

  if (typeof result !== 'string') {
    console.warn(`Translation value is not a string: ${path}`);
    return path;
  }

  // パラメータの置換（例: "Top {count} results" → "Top 10 results"）
  if (params) {
    return result.replace(/\{(\w+)\}/g, (_, key) => {
      return params[key]?.toString() ?? `{${key}}`;
    });
  }

  return result;
}

/** 言語コンテキストの型 */
interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

/** 言語コンテキスト */
const LocaleContext = createContext<LocaleContextType>({
  locale: defaultLocale,
  setLocale: () => {},
});

/**
 * 言語プロバイダー
 * アプリ全体で言語設定を共有する
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  // ブラウザのローカルストレージから言語設定を読み込む
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('mibig-locale') as Locale;
      if (saved === 'en' || saved === 'ja') return saved;
    }
    return defaultLocale;
  });

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    if (typeof window !== 'undefined') {
      localStorage.setItem('mibig-locale', newLocale);
    }
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

/**
 * 翻訳フック
 * @returns t関数 - 翻訳キーを現在の言語に変換
 * @returns locale - 現在の言語コード
 * @returns setLocale - 言語を変更する関数
 *
 * @example
 * const { t, locale, setLocale } = useTranslation();
 * t('home.title') // → "MIBiG Database" (英語) or "MIBiGデータベース" (日本語)
 * setLocale('ja') // 日本語に切り替え
 */
export function useTranslation() {
  const { locale, setLocale } = useContext(LocaleContext);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      return getNestedValue(translations[locale], key, params);
    },
    [locale]
  );

  return { t, locale, setLocale };
}

/**
 * 直接使用するためのt関数（Reactコンポーネント外で使用する場合）
 * デフォルトは英語
 */
export function t(key: string, params?: Record<string, string | number>): string {
  return getNestedValue(en, key, params);
}
